import dayjs from "dayjs";

const STATUS_TEXT_MAP = {
  0: "全部投诉",
  1: "待处理",
  2: "处理中",
  3: "处理完成"
};

const PLATFORM_TEXT_MAP = {
  0: "电话渠道",
  3: "微信",
  4: "支付宝"
};

const CONTENT_MARKERS = [
  "||USER_CREATE_COMPLAINT",
  "||MERCHANT_RESPONSE",
  "||MERCHANT_CONFIRM_COMPLETE",
  "||COMPLAINT_FULL_REFUNDED_SYSTEM_MESSAGE",
  "||COMPLAINT_REFUND_RECEIVED_SYSTEM_MESSAGE",
  "||USER_CONTINUE_COMPLAINT",
  "||USER_RESPONSE",
  "_SYSTEM_MESSAGE",
  "IIPLATFORM HELP APPLICATION",
  "||tag:||times:1||"
];

const toNumber = value => {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }

  const nextValue = Number(value);
  return Number.isNaN(nextValue) ? 0 : nextValue;
};

const fenToYuan = value => {
  return toNumber(value) / 100;
};

const normalizeStatusValue = value => {
  const status = toNumber(value);
  return status || 0;
};

const formatDateTime = (value, boundary) => {
  const instance = value ? dayjs(value) : dayjs();
  const dateValue =
    boundary === "start" ? instance.startOf("day") : instance.endOf("day");
  return dateValue.format("YYYY-MM-DD HH:mm:ss");
};

const stripComplaintContent = content => {
  return CONTENT_MARKERS.reduce((message, marker) => {
    return String(message || "").replaceAll(marker, "");
  }, content || "").trim();
};

const getComplaintStatusText = status => {
  return STATUS_TEXT_MAP[normalizeStatusValue(status)] || "未知状态";
};

const getComplaintListStatus = (record = {}) => {
  const currStatus = normalizeStatusValue(record.currStatus ?? record.curr_status);
  const hasNewMsg = toNumber(record.hasNewMsg ?? record.has_new_msg);

  if (currStatus === 3 && hasNewMsg === 1) {
    return 2;
  }

  return currStatus;
};

const getComplaintListStatusText = (record = {}) => {
  return getComplaintStatusText(getComplaintListStatus(record));
};

const getPlatformValue = (record = {}) => {
  if (
    record.platform !== undefined &&
    record.platform !== null &&
    record.platform !== ""
  ) {
    return normalizeStatusValue(record.platform);
  }

  if (record.complain_channel === 1 || record.complainChannel === 1) {
    return 0;
  }

  return 0;
};

const getPlatformText = (record = {}) => {
  return PLATFORM_TEXT_MAP[getPlatformValue(record)] || "未知渠道";
};

const isPhoneComplaint = (record = {}) => getPlatformValue(record) === 0;
const isWeChatComplaint = (record = {}) => getPlatformValue(record) === 3;
const isAlipayComplaint = (record = {}) => getPlatformValue(record) === 4;

const getOrderRefundStatus = (order = {}) => {
  const explicitStatus = order.refundStatus || order.refund_status;
  if (explicitStatus === "refunded") {
    return "refunded";
  }
  if (explicitStatus === "pending") {
    return "pending";
  }

  const refundTime = order.orderRefundTime || order.order_refund_time;
  if (refundTime) {
    return "refunded";
  }

  const fsmOrderStatus = order.fsmOrderStatus ?? order.fsm_order_status;
  if (toNumber(fsmOrderStatus) === 70) {
    return "refunded";
  }
  if (toNumber(fsmOrderStatus) === 40) {
    return "refunding";
  }

  return "pending";
};

const canRefundOrder = (order = {}) => {
  const refundStatus = getOrderRefundStatus(order);
  return refundStatus !== "refunded" && refundStatus !== "refunding";
};

const getOrderRefundStatusText = (order = {}) => {
  const refundStatus = getOrderRefundStatus(order);
  if (refundStatus === "refunded") {
    return "已退款";
  }
  if (refundStatus === "refunding") {
    return "退款中";
  }
  return "待处理";
};

const getOrderRefundActionState = (order = {}) => {
  const refundStatus = order.refundStatus || getOrderRefundStatus(order);
  if (refundStatus === "refunded") {
    return {
      disabled: true,
      text: "已退款"
    };
  }
  if (refundStatus === "refunding") {
    return {
      disabled: true,
      text: "退款中"
    };
  }
  return {
    disabled: !canRefundOrder(order),
    text: "退款"
  };
};

const adaptOrder = (rawOrder = {}) => {
  const amount =
    rawOrder.amount ?? rawOrder.mfr_order_amount ?? rawOrder.order_amount;
  const orderRefundTime =
    rawOrder.orderRefundTime || rawOrder.order_refund_time || "";
  const fsmOrderStatus = rawOrder.fsmOrderStatus ?? rawOrder.fsm_order_status;
  const nextOrder = {
    id: rawOrder.id,
    appId: rawOrder.appId || rawOrder.app_id || "",
    userId: rawOrder.userId || rawOrder.mfr_uid || "",
    bankTradeCode: rawOrder.bankTradeCode || rawOrder.bank_trade_code || "",
    orderId:
      rawOrder.orderId || rawOrder.mfr_order_id || rawOrder.order_number || "",
    productName: rawOrder.productName || rawOrder.mfr_product_name || "",
    amount: fenToYuan(amount),
    payTime:
      rawOrder.payTime ||
      rawOrder.order_pay_time ||
      rawOrder.mfr_order_createtime ||
      "",
    orderRefundTime,
    fsmOrderStatus:
      fsmOrderStatus === undefined ||
      fsmOrderStatus === null ||
      fsmOrderStatus === ""
        ? null
        : toNumber(fsmOrderStatus),
    raw: rawOrder
  };

  nextOrder.refundStatus = getOrderRefundStatus({
    ...rawOrder,
    ...nextOrder
  });
  nextOrder.refundStatusText = getOrderRefundStatusText(nextOrder);
  nextOrder.refundable = canRefundOrder(nextOrder);

  return nextOrder;
};

const getReplyRole = (reply = {}) => {
  const role = reply.role || reply.operator || "";
  const normalizedRole = String(role).toLowerCase();
  const rawContent = String(reply.content || "");

  if (
    rawContent.includes("||USER_CREATE_COMPLAINT") ||
    rawContent.includes("||USER_CONTINUE_COMPLAINT") ||
    rawContent.includes("||USER_RESPONSE")
  ) {
    return "user";
  }

  if (
    rawContent.includes("||MERCHANT_RESPONSE") ||
    rawContent.includes("||MERCHANT_CONFIRM_COMPLETE")
  ) {
    return "merchant";
  }

  if (
    rawContent.includes("||COMPLAINT_FULL_REFUNDED_SYSTEM_MESSAGE") ||
    rawContent.includes("||COMPLAINT_REFUND_RECEIVED_SYSTEM_MESSAGE") ||
    rawContent.includes("_SYSTEM_MESSAGE") ||
    rawContent.includes("IIPLATFORM HELP APPLICATION")
  ) {
    return "system";
  }

  if (
    normalizedRole === "user" ||
    normalizedRole.includes("用户") ||
    normalizedRole.includes("投诉人")
  ) {
    return "user";
  }
  if (
    normalizedRole === "merchant" ||
    normalizedRole.includes("商家") ||
    normalizedRole.includes("厂商") ||
    normalizedRole.includes("客服")
  ) {
    return "merchant";
  }
  return "system";
};

const getReplyRoleText = role => {
  if (role === "user") {
    return "用户";
  }
  if (role === "merchant") {
    return "商家";
  }
  return "系统";
};

const adaptReplyItem = (reply = {}) => {
  const role = getReplyRole(reply);
  const rawImages =
    reply.images || reply.image_urls || reply.imageUrls || reply.image_url || [];
  const images = Array.isArray(rawImages)
    ? rawImages
    : String(rawImages || "")
      .split(",")
      .map(item => item.trim())
      .filter(Boolean);

  return {
    role,
    roleText: getReplyRoleText(role),
    content: stripComplaintContent(reply.content || ""),
    createdAt: reply.created_at || reply.operate_time || reply.createdAt || "",
    images,
    isInternal: Boolean(reply.is_internal || reply.isInternal),
    raw: reply
  };
};

const getReplyHistory = (detail = {}) => {
  const replyList = detail.history || detail.replys || detail.replies || [];
  return Array.isArray(replyList) ? replyList.map(adaptReplyItem) : [];
};

const adaptComplaintRecord = (rawRecord = {}) => {
  const orderInfo = rawRecord.order_info || {};
  const rawOrderList = Array.isArray(orderInfo.list)
    ? orderInfo.list
    : orderInfo.list
      ? [orderInfo.list]
      : [];
  const orderList = rawOrderList.map(adaptOrder);
  const firstOrder = orderList[0] || null;
  const totalAmount =
    orderInfo.total_amount !== undefined && orderInfo.total_amount !== null
      ? fenToYuan(orderInfo.total_amount)
      : fenToYuan(orderList.reduce((sum, order) => sum + toNumber(order.amount), 0));

  return {
    complainNo: rawRecord.complain_no || "",
    phoneNo: rawRecord.phone_no || "",
    complainContent: stripComplaintContent(rawRecord.complain_content || ""),
    complainTime: rawRecord.complain_time || "",
    hasNewMsg: toNumber(rawRecord.has_new_msg ?? rawRecord.hasNewMsg),
    currStatus: normalizeStatusValue(rawRecord.curr_status),
    currStatusText: getComplaintStatusText(rawRecord.curr_status),
    listStatus: getComplaintListStatus(rawRecord),
    listStatusText: getComplaintListStatusText(rawRecord),
    platform: getPlatformValue(rawRecord),
    platformText: getPlatformText(rawRecord),
    complainChannel: rawRecord.complain_channel ?? rawRecord.complainChannel,
    totalAmount,
    orderList,
    firstOrder,
    forbiddenReply: Boolean(rawRecord.forbidden_reply),
    handleRemark: rawRecord.handle_remark || "",
    replyHistory: getReplyHistory(rawRecord),
    raw: rawRecord
  };
};

const adaptComplaintListResponse = (response = {}) => {
  const rawList = Array.isArray(response.list) ? response.list : [];
  return {
    list: sortComplaintRecords(rawList.map(adaptComplaintRecord)),
    total: toNumber(response.total_num || response.total || 0)
  };
};

const getComplaintListDisplay = (record = {}) => {
  const orderList = Array.isArray(record.orderList) ? record.orderList : [];
  const firstOrder = record.firstOrder || {};
  const productName = firstOrder.productName || "--";
  const listStatus = getComplaintListStatus(record);

  return {
    actionText: listStatus === 3 ? "详情" : "处理",
    hoverEnabled: orderList.length > 1,
    isExpandable: orderList.length > 1,
    productNameText:
      orderList.length > 1 ? `${productName}（连续付费）` : productName,
    showMoreText:
      orderList.length > 1 ? `展开剩余 ${orderList.length - 1} 笔订单` : ""
  };
};

const sortComplaintRecords = (records = []) => {
  return [...records].sort((left, right) => {
    const rightTime = dayjs(
      right.complainTime || right.complain_time || 0
    ).valueOf();
    const leftTime = dayjs(
      left.complainTime || left.complain_time || 0
    ).valueOf();
    return rightTime - leftTime;
  });
};

const includesKeyword = (value, keyword) => {
  return String(value || "")
    .toLowerCase()
    .includes(
      String(keyword || "")
        .trim()
        .toLowerCase()
    );
};

const matchComplaintKeyword = (record = {}, keyword = "") => {
  if (!String(keyword || "").trim()) {
    return true;
  }

  const orderList = Array.isArray(record.orderList) ? record.orderList : [];
  const firstOrder = record.firstOrder || {};

  if (
    includesKeyword(record.complainNo, keyword) ||
    includesKeyword(firstOrder.bankTradeCode, keyword) ||
    includesKeyword(firstOrder.orderId, keyword)
  ) {
    return true;
  }

  return orderList.some(order => {
    return (
      includesKeyword(order.bankTradeCode, keyword) ||
      includesKeyword(order.orderId, keyword)
    );
  });
};

const filterComplaintRecords = (records = [], keyword = "") => {
  return records.filter(record => matchComplaintKeyword(record, keyword));
};

const buildFallbackHistory = (record = {}) => {
  if (Array.isArray(record.replyHistory) && record.replyHistory.length) {
    return record.replyHistory;
  }

  if (isAlipayComplaint(record) && record.handleRemark) {
    return [
      {
        role: "merchant",
        roleText: "商家",
        content: record.handleRemark,
        createdAt: "",
        isInternal: true
      }
    ];
  }

  return [];
};

const getComplaintDetailViewState = (record = {}) => {
  const orderList = Array.isArray(record.orderList) ? record.orderList : [];
  const isAlipay = isAlipayComplaint(record);
  const isWechat = isWeChatComplaint(record);
  const isPhone = isPhoneComplaint(record);
  const hasRefundableOrders = orderList.some(canRefundOrder);
  const hasRefundedOrders = orderList.some(
    order => getOrderRefundStatus(order) === "refunded"
  );
  const hasPartiallyRefundedOrders = hasRefundableOrders && hasRefundedOrders;
  const detailStatus = getComplaintListStatus(record);
  const isCompletedWithoutNewMessage =
    normalizeStatusValue(record.currStatus) === 3 && toNumber(record.hasNewMsg) === 0;
  const canHandleComplete =
    Boolean(record.complainNo) && !isCompletedWithoutNewMessage;
  const canReply =
    Boolean(record.complainNo) &&
    detailStatus !== 3 &&
    !record.forbiddenReply &&
    !isPhone;
  const shouldAutoCompleteAfterAllRefund = isAlipay || isWechat || isPhone;
  let allRefundButtonText = "全部退款";

  if (!hasRefundableOrders) {
    allRefundButtonText = "已全部退款";
  } else if (hasPartiallyRefundedOrders) {
    allRefundButtonText = "继续退款";
  }

  let autoCompleteRemark = "";
  if (isWechat) {
    autoCompleteRemark = "微信渠道已全部退款，自动处理完成";
  } else if (isAlipay) {
    autoCompleteRemark = "支付宝渠道已全部退款，自动处理完成";
  } else if (isPhone) {
    autoCompleteRemark = "电话渠道已全部退款，自动处理完成";
  }

  return {
    allRefundButtonDisabled: !hasRefundableOrders,
    allRefundButtonText,
    autoCompleteRemark,
    canHandleComplete,
    canReply,
    currentStep: Math.max(detailStatus - 1, 0),
    hasPartiallyRefundedOrders,
    hasRefundableOrders,
    historyList: buildFallbackHistory(record),
    isAlipay,
    isPhone,
    isWechat,
    replyButtonText: isAlipay ? "保存备忘" : "发送回复",
    replyPlaceholder: isAlipay
      ? "支付宝渠道：用户无法收到回复，可在下方记录处理备忘。"
      : "请输入回复内容",
    shouldConfirmAllRefund: hasRefundableOrders,
    shouldAutoCompleteAfterAllRefund,
    shouldShowCompletedWechatNotice: isWechat && detailStatus === 3,
    shouldShowPhoneChannelNotice: isPhone,
    shouldShowPartialRefundNotice: isAlipay && hasPartiallyRefundedOrders,
    shouldUsePhoneCompleteConfirm: isPhone && detailStatus !== 3
  };
};

const buildComplaintListParams = (options = {}) => {
  const page = options.page || options.current || options.p_num || 1;
  const pageSize = options.pageSize || options.p_size || 10;
  const orderNo = options.orderNo || options.bank_trade_code || "";
  const phoneNo = options.phoneNo || options.phone_no || options.phone || "";
  const platform = options.platform ?? options.channel;
  const currStatus = options.currStatus ?? options.curr_status ?? 0;
  const dateRange = options.dateRange || options.time || [];
  const now = options.now ? dayjs(options.now) : dayjs();

  const defaultStartTime = now.subtract(1, "month");
  const params = {
    p_num: toNumber(page) || 1,
    p_size: toNumber(pageSize) || 10,
    curr_status: normalizeStatusValue(currStatus),
    start_time: formatDateTime(dateRange[0] || defaultStartTime, "start"),
    end_time: formatDateTime(dateRange[1] || now, "end")
  };

  if (orderNo) {
    params.bank_trade_code = orderNo;
  }
  if (phoneNo) {
    params.phone_no = phoneNo;
  }
  if (platform !== undefined && platform !== null && platform !== "") {
    params.platform = toNumber(platform);
  }

  return params;
};

const buildRefundPayload = (orders = [], refundReason = "同意用户退款申请") => {
  const refundableOrders = orders.filter(canRefundOrder);
  if (!refundableOrders.length) {
    return null;
  }

  const firstOrder = refundableOrders[0];
  return {
    appid: firstOrder.appId || firstOrder.app_id || "",
    order_id: refundableOrders
      .map(order => order.orderId || order.mfr_order_id)
      .filter(Boolean)
      .join(","),
    user_id: firstOrder.userId || firstOrder.mfr_uid || "",
    order_amount: refundableOrders.reduce(
      (sum, order) =>
        sum +
        toNumber(
          order.raw?.mfr_order_amount ??
            order.raw?.order_amount ??
            (order.amount ? Math.round(order.amount * 100) : 0)
        ),
      0
    ),
    refund_reason: refundReason
  };
};

export {
  adaptComplaintListResponse,
  adaptComplaintRecord,
  adaptOrder,
  adaptReplyItem,
  buildComplaintListParams,
  buildFallbackHistory,
  buildRefundPayload,
  canRefundOrder,
  filterComplaintRecords,
  getComplaintDetailViewState,
  getComplaintListDisplay,
  getComplaintListStatus,
  getComplaintListStatusText,
  getComplaintStatusText,
  getOrderRefundActionState,
  getOrderRefundStatus,
  getOrderRefundStatusText,
  getPlatformText,
  getPlatformValue,
  getReplyHistory,
  isAlipayComplaint,
  isPhoneComplaint,
  isWeChatComplaint,
  matchComplaintKeyword,
  PLATFORM_TEXT_MAP,
  sortComplaintRecords,
  STATUS_TEXT_MAP,
  stripComplaintContent
};
